import React, { useState, useMemo } from 'react';
import { Copy, Check, BookOpen, Link2, FileText, Code } from 'lucide-react';
import { sanitizeHtml } from '../../utils/textProcessing.js';
import SourcesCitedBlock from './SourcesCitedBlock.jsx';

const META_TITLE_TARGET = 60;
const META_DESC_TARGET = 160;

const qaColor = (score) =>
  score >= 70 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';

const escapeHtml = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const stripTags = (s) => String(s || '').replace(/<[^>]*>/g, '');

const splitParagraphs = (text) =>
  String(text || '')
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

const buildFaqHtml = (faq) =>
  faq
    .map(
      (f) =>
        `<details>\n  <summary>${escapeHtml(f.question)}</summary>\n  <p>${escapeHtml(
          f.answer
        )}</p>\n</details>`
    )
    .join('\n');

const buildFaqText = (faq) =>
  faq.map((f) => `Q: ${f.question}\nA: ${f.answer}`).join('\n\n');

const buildTestoText = (c) => {
  let out = '';
  if (c.title) out += c.title + '\n\n';
  if (c.hook) out += stripTags(c.hook) + '\n\n';
  (c.sections || []).forEach((s) => {
    out += `## ${s.h2}\n${stripTags(s.content)}\n\n`;
    (s.subsections || []).forEach((sub) => {
      out += `### ${sub.h3}\n${stripTags(sub.content)}\n\n`;
    });
  });
  if (c.conclusion) out += `## Conclusione\n${stripTags(c.conclusion)}\n`;
  return out.trim();
};

// ── Sub-components ──────────────────────────────────────────────────────────

const SectionLabel = ({ children, t }) => (
  <div
    style={{
      fontSize: '10px',
      fontFamily: "'JetBrains Mono', monospace",
      color: t.textMuted || '#7a7a8e',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontWeight: 600,
      marginBottom: '8px',
    }}
  >
    {children}
  </div>
);

const CopyButton = ({ active, onClick, label, t }) => (
  <button
    onClick={onClick}
    style={{
      padding: '5px 12px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: 600,
      cursor: 'pointer',
      border: `1px solid ${t.border || 'rgba(0,0,0,0.1)'}`,
      background: 'transparent',
      color: t.textMuted || '#7a7a8e',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
    }}
  >
    {active ? <><Check size={12} /> Copiato</> : <><Copy size={12} /> {label}</>}
  </button>
);

const MetaRow = ({ label, value, copyId, copiedId, onCopy, target, mono, color, t }) => {
  const len = String(value || '').length;
  const overBudget = target != null && len > target;
  return (
    <div style={{ marginBottom: '12px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '4px',
          gap: '8px',
        }}
      >
        <span
          style={{
            fontSize: '10px',
            fontFamily: "'JetBrains Mono', monospace",
            color: t.textMuted || '#7a7a8e',
            letterSpacing: '0.5px',
            fontWeight: 600,
          }}
        >
          {label}
          {target != null && (
            <span
              style={{
                marginLeft: '8px',
                color: overBudget ? '#EF4444' : t.textFaint || '#888',
                fontWeight: 500,
              }}
            >
              {len}/{target}
            </span>
          )}
        </span>
        <button
          onClick={() => onCopy?.(value || '', copyId)}
          style={{
            padding: '3px 8px',
            borderRadius: '5px',
            fontSize: '10px',
            fontWeight: 600,
            cursor: 'pointer',
            border: `1px solid ${t.border || 'rgba(0,0,0,0.08)'}`,
            background: 'transparent',
            color: t.textMuted || '#7a7a8e',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
          }}
        >
          {copiedId === copyId ? <><Check size={10} /> Copiato</> : <><Copy size={10} /> Copia</>}
        </button>
      </div>
      <div
        style={{
          padding: '10px 12px',
          background: t.inputBg || 'rgba(0,0,0,0.02)',
          border: `1px solid ${t.inputBorder || 'rgba(0,0,0,0.06)'}`,
          borderRadius: '7px',
          fontSize: '13px',
          fontWeight: mono ? 500 : 400,
          color: mono ? color : t.text,
          fontFamily: mono ? "'JetBrains Mono', monospace" : "'DM Sans', sans-serif",
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
      >
        {value || <span style={{ color: t.textFaint, fontStyle: 'italic' }}>—</span>}
      </div>
    </div>
  );
};

// Split text into alternating paragraph runs and inline <ul>/<ol> lists,
// so lists render as siblings instead of nested inside <p>.
const splitTextAndLists = (text) => {
  if (!text) return [];
  const listRegex = /<(ul|ol)\b[^>]*>[\s\S]*?<\/\1>/gi;
  const blocks = [];
  let lastIdx = 0;
  let m;
  while ((m = listRegex.exec(text)) !== null) {
    if (m.index > lastIdx) blocks.push({ type: 'text', value: text.slice(lastIdx, m.index) });
    blocks.push({ type: 'list', value: m[0] });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) blocks.push({ type: 'text', value: text.slice(lastIdx) });
  return blocks.length ? blocks : [{ type: 'text', value: text }];
};

const Paragraphs = ({ text }) => {
  const blocks = splitTextAndLists(text);
  return blocks.flatMap((block, bi) => {
    if (block.type === 'list') {
      return [
        <div
          key={`list-${bi}`}
          style={{ margin: '0 0 12px', lineHeight: 1.75, fontSize: '14px' }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.value) }}
        />,
      ];
    }
    return splitParagraphs(block.value).map((p, i) => (
      <p
        key={`p-${bi}-${i}`}
        style={{ margin: '0 0 12px', lineHeight: 1.75, fontSize: '14px' }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(p) }}
      />
    ));
  });
};

// ── Main component ──────────────────────────────────────────────────────────

const SeoResultEntry = ({
  entry,
  copiedId,
  onCopy,
  w = (o) => `rgba(255,255,255,${o})`,
  t = {},
}) => {
  const r = entry.results || {};
  const c = useMemo(() => r.content || {}, [r.content]);
  const color = entry.modeConfig?.color || '#F59E0B';
  const eId = entry.id || 'seo';

  const [tab, setTab] = useState('testo'); // 'testo' | 'html'

  const qaScore = r.metadata?.qa_score ?? r.metadata?.style_compliance ?? null;
  const faq = Array.isArray(c.faq) ? c.faq : [];
  const internalLinks = Array.isArray(c.internal_links)
    ? c.internal_links.filter((l) => l && l.url)
    : [];
  const wordCount = c.word_count || 0;
  const readingTime = c.reading_time || '';

  const testoText = useMemo(() => buildTestoText(c), [c]);
  const htmlBody = c.body_html || '';

  const tabBtnStyle = (active) => ({
    padding: '7px 16px',
    borderRadius: '7px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: active ? `${color}20` : 'transparent',
    color: active ? color : t.textMuted || '#7a7a8e',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
  });

  return (
    <div>
      {/* Region 2 — QA Score bar */}
      {qaScore != null && (
        <div style={{ marginBottom: '18px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
            }}
          >
            <SectionLabel t={t}>QA Score</SectionLabel>
            <span
              style={{
                fontSize: '13px',
                fontWeight: 700,
                color: qaColor(qaScore),
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {qaScore}/100
            </span>
          </div>
          <div
            style={{
              height: '6px',
              borderRadius: '4px',
              background: w(0.06),
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(100, Math.max(0, qaScore))}%`,
                height: '100%',
                background: qaColor(qaScore),
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </div>
      )}

      {/* Region 3 — Metadata block */}
      <div
        style={{
          padding: '14px 16px',
          background: w(0.02),
          border: `1px solid ${w(0.06)}`,
          borderRadius: '10px',
          marginBottom: '18px',
        }}
      >
        <SectionLabel t={t}>Metadata</SectionLabel>
        <MetaRow
          label="H1 / Titolo"
          value={c.title}
          copyId={`seo-title-${eId}`}
          copiedId={copiedId}
          onCopy={onCopy}
          t={t}
        />
        <MetaRow
          label="Meta Title"
          value={c.meta_title}
          copyId={`seo-meta-title-${eId}`}
          copiedId={copiedId}
          onCopy={onCopy}
          target={META_TITLE_TARGET}
          t={t}
        />
        <MetaRow
          label="Meta Description"
          value={c.meta_description}
          copyId={`seo-meta-desc-${eId}`}
          copiedId={copiedId}
          onCopy={onCopy}
          target={META_DESC_TARGET}
          t={t}
        />
        <MetaRow
          label="URL Slug"
          value={c.slug ? `/${c.slug}` : ''}
          copyId={`seo-slug-${eId}`}
          copiedId={copiedId}
          onCopy={(_, id) => onCopy?.(c.slug || '', id)}
          mono
          color={color}
          t={t}
        />
      </div>

      {/* Region 4 — Content tabs */}
      <div
        style={{
          padding: '14px 16px',
          background: w(0.02),
          border: `1px solid ${w(0.06)}`,
          borderRadius: '10px',
          marginBottom: '18px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '14px',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <div style={{ display: 'flex', gap: '4px' }}>
            <button onClick={() => setTab('testo')} style={tabBtnStyle(tab === 'testo')}>
              <FileText size={12} /> Testo
            </button>
            <button onClick={() => setTab('html')} style={tabBtnStyle(tab === 'html')}>
              <Code size={12} /> HTML
            </button>
          </div>
          <CopyButton
            active={copiedId === `seo-tab-${tab}-${eId}`}
            onClick={() =>
              onCopy?.(tab === 'html' ? htmlBody : testoText, `seo-tab-${tab}-${eId}`)
            }
            label="Copia"
            t={t}
          />
        </div>

        {tab === 'testo' ? (
          <div style={{ color: t.text }}>
            {c.title && (
              <h1
                style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  margin: '0 0 14px',
                  lineHeight: 1.3,
                  color: t.text,
                }}
              >
                {c.title}
              </h1>
            )}
            <Paragraphs text={c.hook} />
            {(c.sections || []).map((s, i) => (
              <div key={i} style={{ marginTop: '20px' }}>
                <h2
                  id={s.heading_id || undefined}
                  style={{
                    fontSize: '17px',
                    fontWeight: 700,
                    color,
                    margin: '0 0 10px',
                    lineHeight: 1.35,
                  }}
                >
                  {s.h2}
                </h2>
                <Paragraphs text={s.content} />
                {(s.subsections || []).map((sub, j) => (
                  <div
                    key={j}
                    style={{
                      marginTop: '14px',
                      paddingLeft: '14px',
                      borderLeft: `2px solid ${color}30`,
                    }}
                  >
                    <h3
                      style={{
                        fontSize: '14.5px',
                        fontWeight: 600,
                        color: t.textSoft || t.text,
                        margin: '0 0 8px',
                        lineHeight: 1.4,
                      }}
                    >
                      {sub.h3}
                    </h3>
                    <Paragraphs text={sub.content} />
                  </div>
                ))}
              </div>
            ))}
            {c.conclusion && (
              <div style={{ marginTop: '20px' }}>
                <h2
                  style={{
                    fontSize: '17px',
                    fontWeight: 700,
                    color,
                    margin: '0 0 10px',
                    lineHeight: 1.35,
                  }}
                >
                  Conclusione
                </h2>
                <Paragraphs text={c.conclusion} />
              </div>
            )}
          </div>
        ) : (
          <>
            {htmlBody && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '5px 10px',
                  borderRadius: '6px',
                  background: 'rgba(16,185,129,0.1)',
                  color: '#10B981',
                  fontSize: '11px',
                  fontWeight: 600,
                  marginBottom: '10px',
                }}
              >
                <Check size={12} /> Ottimizzato da SEO Agent
              </div>
            )}
            <pre
              style={{
                margin: 0,
                padding: '14px',
                background: w(0.04),
                border: `1px solid ${w(0.06)}`,
                borderRadius: '8px',
                fontSize: '11.5px',
                lineHeight: 1.6,
                fontFamily: "'JetBrains Mono', monospace",
                color: t.text,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '480px',
                overflow: 'auto',
              }}
            >
              {htmlBody}
            </pre>
          </>
        )}
      </div>

      {/* Region 5 — Reading-stats strip */}
      {wordCount > 0 && (
        <div
          style={{
            fontSize: '11.5px',
            fontFamily: "'JetBrains Mono', monospace",
            color: t.textMuted || '#7a7a8e',
            marginBottom: '14px',
          }}
        >
          {wordCount} parole{readingTime ? ` • ${readingTime} lettura` : ''}
        </div>
      )}

      {/* Region 6 — FAQ */}
      {faq.length > 0 && (
        <div
          style={{
            marginBottom: '14px',
            padding: '14px',
            background: 'rgba(245,158,11,0.04)',
            borderRadius: '10px',
            border: '1px solid rgba(245,158,11,0.15)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
              flexWrap: 'wrap',
            }}
          >
            <BookOpen size={13} color="#F59E0B" />
            <span
              style={{
                fontSize: '11px',
                fontFamily: "'JetBrains Mono', monospace",
                color: '#F59E0B',
                fontWeight: 600,
              }}
            >
              FAQ ({faq.length})
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
              <button
                onClick={() => onCopy?.(buildFaqHtml(faq), `seo-faq-html-${eId}`)}
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '10.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: `1px solid ${t.border || 'rgba(0,0,0,0.1)'}`,
                  background: 'transparent',
                  color: '#F59E0B',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {copiedId === `seo-faq-html-${eId}` ? (
                  <><Check size={11} /> Copiato</>
                ) : (
                  <><Copy size={11} /> Copia HTML</>
                )}
              </button>
              <button
                onClick={() => onCopy?.(buildFaqText(faq), `seo-faq-text-${eId}`)}
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '10.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: `1px solid ${t.border || 'rgba(0,0,0,0.1)'}`,
                  background: 'transparent',
                  color: '#F59E0B',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {copiedId === `seo-faq-text-${eId}` ? (
                  <><Check size={11} /> Copiato</>
                ) : (
                  <><Copy size={11} /> Copia Testo</>
                )}
              </button>
            </div>
          </div>
          {faq.map((f, i) => (
            <div
              key={i}
              style={{
                marginBottom: '10px',
                padding: '12px',
                background: w(0.03),
                borderRadius: '8px',
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '13px',
                  marginBottom: '6px',
                  color: t.text,
                }}
              >
                {f.question}
              </div>
              <div
                style={{
                  fontSize: '12.5px',
                  color: t.textMuted || '#7a7a8e',
                  lineHeight: 1.6,
                }}
              >
                {f.answer}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Region 7 — Principali Fonti da Includere */}
      <SourcesCitedBlock
        sources={r.sources?.cited}
        copiedId={copiedId}
        onCopy={onCopy}
        entryId={eId}
        w={w}
        t={t}
      />

      {/* Region 8 — Internal Links */}
      {internalLinks.length > 0 && (
        <div
          style={{
            marginTop: '14px',
            padding: '14px',
            background: 'rgba(99,102,241,0.04)',
            borderRadius: '10px',
            border: '1px solid rgba(99,102,241,0.12)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '10px',
            }}
          >
            <Link2 size={13} color="#6366F1" />
            <span
              style={{
                fontSize: '11px',
                fontFamily: "'JetBrains Mono', monospace",
                color: '#6366F1',
                fontWeight: 600,
              }}
            >
              INTERNAL LINKS ({internalLinks.length})
            </span>
          </div>
          {internalLinks.map((link, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 10px',
                background: w(0.03),
                borderRadius: '6px',
                fontSize: '12px',
                marginBottom: '4px',
                flexWrap: 'wrap',
              }}
              title={link.context || undefined}
            >
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#6366F1',
                  textDecoration: 'none',
                  fontSize: '11px',
                  wordBreak: 'break-all',
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {link.url}
              </a>
              <span style={{ color: t.textFaint || '#555568', fontSize: '10px' }}>→</span>
              <code
                style={{
                  background: 'rgba(16,163,127,0.1)',
                  color: '#10A37F',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 500,
                }}
              >
                {link.anchor_text}
              </code>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SeoResultEntry;
